import styled from "styled-components";

import { SCAN_TO_DEFAULT_MAIN_COLOR } from "../shared/constants";

// ─── Layout ──────────────────────────────────────────────

export const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr 480px;
    gap: 30px;
    padding: 20px;
    height: 100%;

    @media (max-width: 1200px) {
        grid-template-columns: 1fr 400px;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 576px) {
        padding: 10px;
        gap: 15px;
    }
`;

export const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 25px;
    overflow-y: auto;
`;

export const RightSection = styled.div<{ isVisible: boolean }>`
    display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
    flex-direction: column;
    align-items: center;
    padding: 20px;
    background-color: #f5f5f5;
    border-radius: 12px;
    transition: all 0.3s ease;

    @media (max-width: 992px) {
        padding: 15px;
        position: fixed;
        top: 0;
        right: ${({ isVisible }) => (isVisible ? "0" : "-100%")};
        width: 100%;
        max-width: 450px;
        height: 100vh;
        z-index: 999;
        overflow-y: auto;
        box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
        display: flex;
    }

    @media (max-width: 576px) {
        padding: 10px;
        max-width: 100%;
    }
`;

export const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
`;

export const SectionTitle = styled.h5`
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0;
`;

export const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const ToggleLabel = styled.span`
    font-size: 14px;
    color: #666;
`;

export const Toggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 50px;
    height: 26px;
    border-radius: 13px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ isActive }) => (isActive ? "27px" : "3px")};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const Card = styled.div`
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
`;

export const ImageSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

export const ImageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

export const Select = styled.select`
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    color: #333;
    background-color: #fff;
    min-width: 200px;
`;

export const AddButton = styled.button<{ mainColor: string }>`
    padding: 8px 20px;
    background-color: ${({ mainColor }) => mainColor};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

export const TableWrapper = styled.div`
    max-height: 400px;
    overflow-y: auto;
    margin-top: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow-x: auto;

    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
            background: #555;
        }
    }
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    min-width: 500px;
`;

export const Th = styled.th`
    text-align: left;
    padding: 10px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    border-bottom: 2px solid #e0e0e0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 10;
`;

export const Td = styled.td`
    padding: 12px;
    font-size: 13px;
    color: #333;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
    white-space: nowrap;
`;

export const DragHandle = styled.div`
    cursor: grab;
    color: #999;
    display: flex;
    align-items: center;
    gap: 8px;

    &:active {
        cursor: grabbing;
    }
`;

export const OrderInput = styled.input`
    width: 40px;
    padding: 4px 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 13px;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #999;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

export const ImageThumbnail = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 6px;
    overflow: hidden;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export const SmallToggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 40px;
    height: 22px;
    border-radius: 11px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "20px" : "2px")};
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const ActionsCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`;

export const IconButton = styled.button<{ color?: string }>`
    background: none;
    border: none;
    color: ${({ color }) => color || "#666"};
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ color }) => (color ? `${color}20` : "#f0f0f0")};
        transform: scale(1.1);
    }

    svg {
        width: 24px;
        height: 24px;
    }
`;

export const DeleteButton = styled(IconButton)`
    color: #dc3545;

    &:hover {
        background-color: #dc354520;
    }
`;

export const ViewButton = styled(IconButton)`
    color: #007bff;

    &:hover {
        background-color: #007bff20;
    }
`;

export const PreviewToggleButton = styled.button<{ mainColor: string }>`
    position: fixed;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${({ mainColor }) => mainColor};
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    z-index: 1000;

    &:hover {
        transform: translateY(-50%) scale(1.1);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    svg {
        width: 20px;
        height: 20px;
    }

    @media (max-width: 992px) {
        display: flex;
    }

    @media (min-width: 993px) {
        display: none;
    }
`;

export const ClosePreviewButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.1);
    color: #333;
    border: none;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
        background-color: rgba(0, 0, 0, 0.2);
    }

    svg {
        width: 18px;
        height: 18px;
    }

    @media (max-width: 992px) {
        display: flex;
    }
`;

export const OpenInNewWindowButton = styled.button<{ mainColor: string }>`
    position: absolute;
    top: 10px;
    right: 50px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${({ mainColor }) => mainColor};
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
        opacity: 0.9;
        transform: scale(1.05);
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const PreviewOverlay = styled.div<{ isVisible: boolean }>`
    display: none;

    @media (max-width: 992px) {
        display: ${({ isVisible }) => (isVisible ? "block" : "none")};
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 998;
        transition: opacity 0.3s ease;
    }
`;

// ─── Language Tabs ───────────────────────────────────────

export const LanguageTabs = styled.div`
    display: flex;
    gap: 5px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    @media (max-width: 576px) {
        gap: 8px;
        justify-content: center;
    }
`;

export const LanguageTab = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    padding: 6px 16px;
    border: 1px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    border-radius: 20px;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#fff"};
    color: ${({ isActive }) => (isActive ? "#fff" : "#333")};
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

// ─── Input ───────────────────────────────────────────────

export const InputRow = styled.div`
    display: grid;
    grid-template-columns: 100px 1fr 2fr;
    gap: 10px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    @media (max-width: 768px) {
        grid-template-columns: 80px 1fr 1.5fr;
        gap: 8px;
        padding: 8px 0;
    }

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
        gap: 10px;
        padding: 15px 0;
    }
`;

export const InputLabel = styled.span`
    font-size: 13px;
    color: #666;
`;

export const Input = styled.input`
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    width: 100%;

    &:focus {
        outline: none;
        border-color: #999;
    }
`;

export const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ResetButton = styled.button<{ mainColor: string }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ mainColor }) => mainColor};
    transition: background-color 0.2s ease;
    border-radius: 4px;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}20`};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

// ─── Modal ───────────────────────────────────────────────

export const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

export const ModalContent = styled.div`
    background-color: white;
    border-radius: 12px;
    padding: 30px;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

export const ModalTitle = styled.h3`
    margin: 0 0 15px 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

export const ModalText = styled.p`
    margin: 0 0 25px 0;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
`;

export const ModalButtons = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
`;

export const ModalButton = styled.button<{
    variant?: "primary" | "danger";
    mainColor?: string;
}>`
    padding: 10px 24px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    ${({ variant, mainColor }) =>
        variant === "danger"
            ? `
        background-color: ${mainColor || SCAN_TO_DEFAULT_MAIN_COLOR};
        color: white;
        border: 2px solid ${mainColor || SCAN_TO_DEFAULT_MAIN_COLOR};
        &:hover {
            opacity: 0.9;
        }
    `
            : `
        background-color: white;
        color: #666;
        border: 2px solid #ddd;
        &:hover {
            border-color: #999;
            color: #333;
        }
    `}
`;
